import React, {
  createContext, useContext, useState, useEffect, useMemo,
} from 'react';

import { queryClient } from '@vlabs/api';

import type { UserPermissions, AppName, PermissionOverride } from '../types';
import { PermissionEngine } from './PermissionEngine';
import { usePermissionQuery } from './PermissionQuery';

type ApiQueryClient = typeof queryClient;

interface PermissionContextValue {
  engine: PermissionEngine;
  userPermissions: UserPermissions | null;
  isLoading: boolean;
  error: Error | null;
  override: PermissionOverride | null;
  setOverride: (override: PermissionOverride | null) => void;
  resetOverride: () => void;
  refetch: () => Promise<unknown>;
  queryClient: ApiQueryClient;
}

const PermissionContext = createContext<PermissionContextValue | null>(null);

interface PermissionProviderProps {
  children: React.ReactNode;
  appName: AppName;
  fetchPermissions: () => Promise<unknown>;
  refetchInterval?: number;
  refetchOnWindowFocus?: boolean;
  onPermissionsChange?: (perms: UserPermissions) => void;
}

// Время жизни кэша прав: после истечения права заново грузятся с бэка.
// sessionStorage — чтобы кэш сам сбрасывался в новых вкладках/сессиях.
const PERMISSIONS_CACHE_TTL_MS = 60 * 1000;

function getPermissionsStorageKey(appName: AppName): string {
  return `permissions_${appName}`;
}

function createPersister(appName: AppName) {
  const storageKey = getPermissionsStorageKey(appName);
  return {
    persistClient: async (client: ApiQueryClient) => {
      try {
        const data = client.getQueryCache().findAll({ queryKey: ['permissions', appName] });
        const query = data[0];
        if (query?.state.data) {
          sessionStorage.setItem(storageKey, JSON.stringify({
            savedAt: Date.now(),
            data: query.state.data,
          }));
        }
      } catch {
        // ignore
      }
    },
    restoreClient: async (client: ApiQueryClient) => {
      try {
        const stored = sessionStorage.getItem(storageKey);
        if (!stored) return;
        const parsed = JSON.parse(stored);
        if (
          typeof parsed?.savedAt !== 'number'
          || Date.now() - parsed.savedAt > PERMISSIONS_CACHE_TTL_MS
        ) {
          sessionStorage.removeItem(storageKey);
          return;
        }
        client.setQueryData(['permissions', appName], parsed.data);
      } catch {
        // ignore
      }
    },
    removeClient: () => {
      sessionStorage.removeItem(storageKey);
    },
  };
}

/** Сброс кэша прав приложения — вызывается при logout, чтобы права одного
 * пользователя не протекли следующему через sessionStorage. */
export function clearPermissionsCache(appName: AppName): void {
  try {
    sessionStorage.removeItem(getPermissionsStorageKey(appName));
  } catch {
    // ignore
  }
}

export function PermissionProvider({
  children,
  appName,
  fetchPermissions,
  refetchInterval = 5 * 60 * 1000,
  refetchOnWindowFocus = true,
  onPermissionsChange = undefined,
}: PermissionProviderProps): React.JSX.Element {
  const [override, setOverride] = useState<PermissionOverride | null>(null);

  useEffect(() => {
    const persister = createPersister(appName);
    persister.restoreClient(queryClient);

    const interval = setInterval(() => {
      persister.persistClient(queryClient);
    }, 1000);

    return () => {
      clearInterval(interval);
      persister.persistClient(queryClient);
    };
  }, [appName]);

  const {
    data: userPermissions,
    isLoading,
    error,
    refetch,
  } = usePermissionQuery({
    appName,
    fetchPermissions,
    refetchInterval,
    refetchOnWindowFocus,
  });

  useEffect(() => {
    if (userPermissions && onPermissionsChange) {
      onPermissionsChange(userPermissions);
    }
  }, [userPermissions, onPermissionsChange]);

  const effectivePermissions = useMemo(() => {
    if (!userPermissions) return ({});
    if (!override) return userPermissions;

    const base = { ...userPermissions };
    if (override.allow) {
      Object.entries(override.allow).forEach(([resource, actions]) => {
        if (Array.isArray(actions)) {
          (base as Record<string, string[]>)[resource] = [
            ...new Set([...((base as Record<string, string[]>)[resource] ?? []), ...actions]),
          ];
        }
      });
    }
    if (override.deny) {
      Object.entries(override.deny).forEach(([resource, actions]) => {
        if (Array.isArray(actions)) {
          (base as Record<string, string[]>)[resource] = (
            (base as Record<string, string[]>)[resource] ?? []
          ).filter((a: string) => !actions.includes(a));
        }
      });
    }
    return base;
  }, [userPermissions, override]);

  const engine = useMemo(() => {
    const isReady = !!userPermissions;
    return new PermissionEngine(effectivePermissions, isReady);
  }, [effectivePermissions, userPermissions]);

  const resetOverride = () => {
    setOverride(null);
  };

  const value = useMemo(
    () => ({
      engine,
      userPermissions: effectivePermissions,
      isLoading,
      error,
      override,
      setOverride: ($override: PermissionOverride | null) => {
        setOverride($override);
      },
      resetOverride,
      refetch,
      queryClient,
    }),
    [engine, effectivePermissions, isLoading, error, override, refetch],
  );

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
}

export function usePermissionContext(): PermissionContextValue {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissionContext must be used within PermissionProvider');
  }
  return context;
}

export function usePermissions(): PermissionEngine {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within PermissionProvider');
  }
  return context.engine;
}
